/**
 * Kundenlisten aus XLSX und CSV lesen — im Browser.
 *
 * Warum nicht auf dem Server: Der Workers-Free-Plan gibt 10 ms CPU pro
 * Request. Eine Tabelle mit einigen hundert Zeilen zu entpacken und zu
 * parsen kostet ein Vielfaches davon. Der Browser hat das Budget, der
 * Worker bekommt nur noch fertige Zeilen.
 *
 * Nebeneffekt: Der Nutzer sieht die erkannten Zeilen samt Spaltenzuordnung,
 * bevor irgendetwas gespeichert wird.
 */

import type { KundeEingabe } from "../shared/types.js";

/**
 * Spaltenerkennung. Der Kunde liefert seine Liste, wie sie eben aussieht —
 * die Überschriften sind nicht verhandelbar. Deshalb mehrere Schreibweisen
 * je Feld, verglichen wird kleingeschrieben und ohne Sonderzeichen.
 */
const SPALTEN: Record<keyof KundeEingabe, string[]> = {
  kundennr: ["kundennr", "kundennummer", "kdnr", "kdnummer", "nummer", "nr", "kundenid"],
  firma: ["firma", "firmenname", "name", "kunde", "kundenname", "unternehmen", "bezeichnung"],
  strasse: ["strasse", "straße", "strassehausnr", "adresse", "anschrift", "str"],
  plz: ["plz", "postleitzahl", "zip"],
  ort: ["ort", "stadt", "city"],
  land: ["land", "country", "lkz"],
  ust_id: ["ustid", "ustidnr", "umsatzsteuerid", "vatid", "steuernummer"],
  ansprechpartner: ["ansprechpartner", "kontakt", "kontaktperson", "ap"],
  email: ["email", "mail", "emailadresse", "epost"],
  telefon: ["telefon", "tel", "telefonnummer", "phone", "fon"],
  bemerkung: ["bemerkung", "notiz", "hinweis", "kommentar", "anmerkung"],
  zahlungsziel_tage: ["zahlungsziel", "zahlungszieltage", "zahlungsfrist", "zieltage"],
  entfernung_km: ["entfernung", "entfernungkm", "km", "kilometer", "distanz"],
};

const normalisiere = (s: string) =>
  s
    .toLowerCase()
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]/g, "");

export interface DateiErgebnis {
  kunden: KundeEingabe[];
  /** Welche Tabellenspalte auf welches Feld gelegt wurde — für die Anzeige. */
  zuordnung: { spalte: string; feld: string }[];
  /** Überschriften, für die es kein Feld gibt. */
  ignoriert: string[];
}

function ordneZu(ueberschriften: string[]): {
  map: Map<number, keyof KundeEingabe>;
  ignoriert: string[];
} {
  const map = new Map<number, keyof KundeEingabe>();
  const ignoriert: string[] = [];
  const belegt = new Set<keyof KundeEingabe>();

  for (const [i, roh] of ueberschriften.entries()) {
    const norm = normalisiere(String(roh ?? ""));
    if (!norm) continue;

    const treffer = (Object.keys(SPALTEN) as (keyof KundeEingabe)[]).find(
      (feld) => !belegt.has(feld) && SPALTEN[feld].includes(norm),
    );

    if (treffer) {
      map.set(i, treffer);
      belegt.add(treffer);
    } else {
      ignoriert.push(String(roh));
    }
  }
  return { map, ignoriert };
}

/**
 * Excel liefert PLZ oft als Zahl und verliert dabei die führende Null:
 * 01067 wird zu 1067. Die wird hier wieder aufgefüllt.
 *
 * Nur bei rein numerischen Werten — sonst würde aus einem Tippfehler wie
 * „ABC“ ein „00ABC“, und die Prüfung im Worker meldete einen Fehler, der
 * in der Datei so gar nicht steht.
 */
function normalisierePlz(text: string): string {
  return /^\d{1,5}$/.test(text) ? text.padStart(5, "0") : text;
}

function baueKunden(
  zeilen: unknown[][],
  map: Map<number, keyof KundeEingabe>,
): KundeEingabe[] {
  const kunden: KundeEingabe[] = [];

  for (const zeile of zeilen) {
    const k: Record<string, unknown> = {};
    for (const [i, feld] of map) {
      const wert = zeile[i];
      if (wert === undefined || wert === null || wert === "") continue;
      const text = String(wert).trim();
      k[feld] =
        feld === "zahlungsziel_tage"
          ? Math.trunc(Number(text.replace(/\D/g, "")) || 14)
          : // Entfernung darf fehlen — dann bleibt das Feld leer statt auf 0
            // zu fallen, sonst rechnete die Fracht später mit Nulldistanz.
            feld === "entfernung_km"
            ? (Number(text.replace(/[^\d]/g, "")) || null)
            : feld === "plz"
              ? normalisierePlz(text)
              : text;
    }
    // Zeilen ohne Firma sind Leerzeilen oder Zwischenüberschriften.
    if (k.firma) kunden.push(k as unknown as KundeEingabe);
  }
  return kunden;
}

/** Erkennt das Trennzeichen anhand der Kopfzeile: Semikolon, Komma oder Tab. */
function erkenneTrenner(kopfzeile: string): string {
  const kandidaten = [";", ",", "\t"];
  return kandidaten.reduce((a, b) =>
    (kopfzeile.split(b).length > kopfzeile.split(a).length ? b : a),
  );
}

/** Minimaler CSV-Leser mit Unterstützung für Anführungszeichen. */
function leseCsv(text: string, trenner: string): string[][] {
  const zeilen: string[][] = [];
  let feld = "";
  let zeile: string[] = [];
  let inAnfuehrung = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inAnfuehrung) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          feld += '"';
          i++;
        } else inAnfuehrung = false;
      } else feld += c;
    } else if (c === '"') inAnfuehrung = true;
    else if (c === trenner) {
      zeile.push(feld);
      feld = "";
    } else if (c === "\n") {
      zeile.push(feld.replace(/\r$/, ""));
      zeilen.push(zeile);
      zeile = [];
      feld = "";
    } else feld += c;
  }
  if (feld || zeile.length) {
    zeile.push(feld.replace(/\r$/, ""));
    zeilen.push(zeile);
  }
  return zeilen.filter((z) => z.some((f) => f.trim() !== ""));
}

export async function leseKundendatei(datei: File): Promise<DateiErgebnis> {
  const endung = datei.name.toLowerCase().split(".").pop() ?? "";
  let raster: unknown[][];

  if (endung === "csv" || endung === "txt") {
    let text = await datei.text();
    // Excel schreibt CSV mit BOM; das würde die erste Überschrift zerstören.
    if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
    raster = leseCsv(text, erkenneTrenner(text.split("\n")[0] ?? ""));
  } else {
    // SheetJS nur bei Bedarf laden — es ist der mit Abstand größte Brocken
    // im Bundle und wird auf allen anderen Seiten nicht gebraucht.
    const XLSX = await import("xlsx");
    const mappe = XLSX.read(await datei.arrayBuffer(), { type: "array" });
    const blatt = mappe.Sheets[mappe.SheetNames[0]];
    if (!blatt) throw new Error("Die Datei enthält kein lesbares Tabellenblatt.");
    raster = XLSX.utils.sheet_to_json<unknown[]>(blatt, { header: 1, blankrows: false, raw: false });
  }

  if (!raster.length) throw new Error("Die Datei ist leer.");

  const ueberschriften = (raster[0] as unknown[]).map((h) => String(h ?? ""));
  const { map, ignoriert } = ordneZu(ueberschriften);

  if (![...map.values()].includes("firma"))
    throw new Error(
      "Keine Spalte für den Firmennamen gefunden. Erwartet wird eine Überschrift " +
        "wie „Firma“, „Name“ oder „Kunde“.",
    );

  return {
    kunden: baueKunden(raster.slice(1) as unknown[][], map),
    zuordnung: [...map.entries()].map(([i, feld]) => ({
      spalte: ueberschriften[i],
      feld: String(feld),
    })),
    ignoriert,
  };
}

/** Vorlage zum Herunterladen, damit niemand die Spaltennamen raten muss. */
export function vorlageCsv(): string {
  return [
    "Kundennummer;Firma;Straße;PLZ;Ort;Ansprechpartner;E-Mail;Telefon;USt-IdNr.;Zahlungsziel;Bemerkung",
    "K-10001;Bäckerei Musterbrot GmbH;Am Markt 3;31134;Hildesheim;Frau Schulz;info@musterbrot.de;05121 12345;DE123456789;14;",
    ";Backhaus Leinetal KG;Industriestr. 12;30880;Laatzen;Herr Bergmann;;;;30;Kundennummer wird vergeben",
  ].join("\n");
}
